import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { isValidRoleId } from "../data/interviewRoleProfiles"

const STORAGE_KEY = "interview-prep-role-v1"
const STORAGE_VERSION = 1

const InterviewRoleContext = createContext(null)

function readStoredRoleId() {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed?.version !== STORAGE_VERSION) return null
    if (parsed.roleId == null || parsed.roleId === "") return null
    return isValidRoleId(parsed.roleId) ? parsed.roleId : null
  } catch {
    return null
  }
}

function writeStoredRoleId(roleId) {
  if (typeof window === "undefined") return
  try {
    if (roleId && isValidRoleId(roleId)) {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ version: STORAGE_VERSION, roleId }),
      )
      return
    }
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // quota, private mode, or disabled storage
  }
}

export function InterviewRoleProvider({ children }) {
  const [roleId, setRoleIdState] = useState(null)
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    setRoleIdState(readStoredRoleId())
    setHydrated(true)
  }, [])

  const setRoleId = useCallback(nextRoleId => {
    const validated =
      nextRoleId == null || nextRoleId === ""
        ? null
        : isValidRoleId(nextRoleId)
          ? nextRoleId
          : null
    setRoleIdState(validated)
    writeStoredRoleId(validated)
  }, [])

  const clearRole = useCallback(() => {
    setRoleIdState(null)
    writeStoredRoleId(null)
  }, [])

  const value = useMemo(
    () => ({
      roleId,
      hydrated,
      setRoleId,
      clearRole,
    }),
    [roleId, hydrated, setRoleId, clearRole],
  )

  return (
    <InterviewRoleContext.Provider value={value}>
      {children}
    </InterviewRoleContext.Provider>
  )
}

export function useInterviewRole() {
  const context = useContext(InterviewRoleContext)
  if (!context) {
    throw new Error(
      "useInterviewRole must be used within an InterviewRoleProvider",
    )
  }
  return context
}

export { STORAGE_KEY, STORAGE_VERSION }
